/**
 * TrashPage — Admin → Trash. Every soft-deleted row (clients, engagements,
 * proposals, compliance items, invoices, meetings) with Restore and
 * Permanent-delete. Restore needs Admin access; permanent delete needs the
 * CEO-only trash.permanent_delete capability and a hard confirm (type the
 * row title). Bulk purge: checkboxes + select-all (selections persist across
 * entity tabs), ONE typed confirmation for the whole batch, per-type count
 * summary. Backed by /v1/admin/trash (+ /v1/admin/trash/bulk-delete).
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RotateCcw, Trash2 } from 'lucide-react';
import { getAccessToken, authenticatedFetch as fetch } from '../../lib/auth-context.js';
import { httpError } from '../../lib/http-error.js';
import { useCapabilities } from '../../hooks/useCapabilities.js';
import { TableSkeletonRows } from '../../components/Skeleton.js';
import { EmptyState } from '../../components/EmptyState.js';
import { ConfirmModal } from '../ClientsPage.js';
import { fmtDateTime } from '../../lib/date.js';

const ENTITIES = [
  { key: 'clients', label: 'Clients' },
  { key: 'engagements', label: 'Engagements' },
  { key: 'proposals', label: 'Proposals' },
  { key: 'compliance-items', label: 'Compliance items' },
  { key: 'invoices', label: 'Invoices' },
  { key: 'meetings', label: 'Meetings' },
] as const;
type EntityKey = (typeof ENTITIES)[number]['key'];
const ENTITY_LABEL = Object.fromEntries(ENTITIES.map((e) => [e.key, e.label.toLowerCase()])) as Record<EntityKey, string>;

interface TrashRow {
  id: string;
  title: string;
  subtitle: string | null;
  deletedAt: string;
}

interface BulkPurgeResult {
  deleted: Partial<Record<EntityKey, number>>;
  blocked: { entity: EntityKey; id: string }[];
  notInTrash: number;
}

export function TrashPage() {
  const { canDo } = useCapabilities();
  const canPurge = canDo('trash.permanent_delete');
  const queryClient = useQueryClient();
  const [entity, setEntity] = useState<EntityKey>('clients');
  const [purging, setPurging] = useState<TrashRow | null>(null);
  const [confirmText, setConfirmText] = useState('');
  // Bulk selection persists across entity tabs so a mixed sweep is one action.
  const [selectedByEntity, setSelectedByEntity] = useState<Partial<Record<EntityKey, Set<string>>>>({});
  const [bulkConfirming, setBulkConfirming] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [bulkResult, setBulkResult] = useState<BulkPurgeResult | null>(null);

  const list = useQuery({
    queryKey: ['admin-trash', entity],
    queryFn: async () => {
      const res = await fetch(`/v1/admin/trash?entity=${entity}`, {
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw await httpError(res);
      const body = await res.json() as { ok: true; data: { items: TrashRow[] } };
      return body.data.items;
    },
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin-trash'] });

  const entitySelection = selectedByEntity[entity] ?? new Set<string>();
  const selectedTotal = Object.values(selectedByEntity).reduce((a, s) => a + (s?.size ?? 0), 0);
  const selectedBreakdown = ENTITIES
    .filter((e) => (selectedByEntity[e.key]?.size ?? 0) > 0)
    .map((e) => `${selectedByEntity[e.key]!.size} ${ENTITY_LABEL[e.key]}`);

  function toggleRow(id: string) {
    setSelectedByEntity((prev) => {
      const next = { ...prev };
      const set = new Set(next[entity] ?? []);
      if (set.has(id)) set.delete(id); else set.add(id);
      next[entity] = set;
      return next;
    });
  }

  function toggleAllVisible() {
    const rows = list.data ?? [];
    const allSelected = rows.length > 0 && rows.every((r) => entitySelection.has(r.id));
    setSelectedByEntity((prev) => ({
      ...prev,
      [entity]: allSelected ? new Set<string>() : new Set(rows.map((r) => r.id)),
    }));
  }

  const restore = useMutation({
    mutationFn: async (row: TrashRow) => {
      const res = await fetch(`/v1/admin/trash/${entity}/${row.id}/restore`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw await httpError(res);
    },
    onSuccess: invalidate,
  });

  const purge = useMutation({
    mutationFn: async (row: TrashRow) => {
      const res = await fetch(`/v1/admin/trash/${entity}/${row.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw await httpError(res);
    },
    onSuccess: () => {
      invalidate();
      setPurging(null);
      setConfirmText('');
    },
  });

  const bulkPurge = useMutation({
    mutationFn: async () => {
      const items = ENTITIES.flatMap((e) =>
        [...(selectedByEntity[e.key] ?? [])].map((id) => ({ entity: e.key, id })),
      );
      const res = await fetch('/v1/admin/trash/bulk-delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken() ?? ''}` },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) throw await httpError(res);
      const body = await res.json() as { ok: true; data: BulkPurgeResult };
      return body.data;
    },
    onSuccess: (result) => {
      invalidate();
      setBulkResult(result);
      setSelectedByEntity({});
      setBulkConfirming(false);
      setBulkText('');
    },
  });

  const colSpan = canPurge ? 5 : 4;
  const allVisibleSelected = (list.data?.length ?? 0) > 0 && (list.data ?? []).every((r) => entitySelection.has(r.id));

  return (
    <section className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-white">Trash</h2>
          <p className="text-sm text-muted">
            Soft-deleted rows. Restore brings a row back; permanent delete removes it forever.
          </p>
        </div>
        <div className="flex flex-wrap gap-1 bg-bg-panel border border-border rounded-lg p-1">
          {ENTITIES.map((e) => (
            <button
              key={e.key}
              onClick={() => setEntity(e.key)}
              aria-pressed={entity === e.key}
              className={`px-3 py-1.5 text-sm rounded-md ${entity === e.key ? 'bg-bg-hover text-white' : 'text-muted hover:text-white'}`}
            >
              {e.label}
              {(selectedByEntity[e.key]?.size ?? 0) > 0 && (
                <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-rose-900 text-rose-200 text-[10px] font-semibold">
                  {selectedByEntity[e.key]!.size}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {bulkResult && (
        <div className="bg-bg-panel border border-border rounded-xl px-4 py-3 text-sm space-y-1">
          <div className="flex items-start justify-between gap-3">
            <span className="text-emerald-300">
              {Object.values(bulkResult.deleted).reduce((a, b) => a + (b ?? 0), 0) > 0
                ? `Permanently deleted ${ENTITIES.filter((e) => bulkResult.deleted[e.key]).map((e) => `${bulkResult.deleted[e.key]} ${ENTITY_LABEL[e.key]}`).join(', ')}.`
                : 'Nothing was permanently deleted.'}
            </span>
            <button onClick={() => setBulkResult(null)} className="text-muted hover:text-white shrink-0" aria-label="Dismiss">✕</button>
          </div>
          {bulkResult.blocked.length > 0 && (
            <p className="text-amber-300">{bulkResult.blocked.length} row(s) skipped — still linked to other records (delete those first).</p>
          )}
          {bulkResult.notInTrash > 0 && (
            <p className="text-muted">{bulkResult.notInTrash} selected row(s) were no longer in the trash.</p>
          )}
        </div>
      )}

      {canPurge && selectedTotal > 0 && (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 bg-rose-950/30 border border-rose-800 rounded-lg px-4 py-2.5">
          <span className="text-sm text-rose-200">
            {selectedTotal} selected ({selectedBreakdown.join(', ')})
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setSelectedByEntity({})}
              className="text-sm text-muted hover:text-white px-3 py-1.5"
            >
              Clear selection
            </button>
            <button
              onClick={() => { setBulkConfirming(true); setBulkText(''); }}
              className="bg-rose-700 hover:bg-rose-600 text-oncolor text-sm font-medium px-3 py-1.5 rounded-lg"
            >
              Permanently delete {selectedTotal} item(s)
            </button>
          </div>
        </div>
      )}

      <div className="bg-bg-panel border border-border rounded-xl overflow-hidden overflow-x-auto">
        <table className="w-full text-sm min-w-[560px]">
          <thead>
            <tr className="text-left text-xs text-muted bg-bg-card border-b border-border">
              {canPurge && (
                <th className="px-3 py-3 font-medium w-8">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleAllVisible}
                    aria-label="Select all rows on this tab"
                    className="w-4 h-4 accent-emerald-500 cursor-pointer align-middle"
                  />
                </th>
              )}
              <th className="px-4 py-3 font-medium">Row</th>
              <th className="px-4 py-3 font-medium">Details</th>
              <th className="px-4 py-3 font-medium">Deleted</th>
              <th className="px-4 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.isLoading && <TableSkeletonRows colSpan={colSpan} />}
            {list.error && (
              <tr><td colSpan={colSpan} className="px-4 py-8 text-center text-rose-400">{(list.error as Error).message}</td></tr>
            )}
            {list.data?.length === 0 && (
              <tr><td colSpan={colSpan}><EmptyState icon={Trash2} title="Trash is empty." subtitle="Soft-deleted rows will appear here." /></td></tr>
            )}
            {list.data?.map((row) => (
              <tr key={row.id} className="border-b border-border/50 hover:bg-bg-hover/40">
                {canPurge && (
                  <td className="px-3 py-3">
                    <input
                      type="checkbox"
                      checked={entitySelection.has(row.id)}
                      onChange={() => toggleRow(row.id)}
                      aria-label={`Select ${row.title}`}
                      className="w-4 h-4 accent-emerald-500 cursor-pointer align-middle"
                    />
                  </td>
                )}
                <td className="px-4 py-3 text-white">{row.title}</td>
                <td className="px-4 py-3 text-muted">{row.subtitle ?? '—'}</td>
                <td className="px-4 py-3 text-muted">{fmtDateTime(row.deletedAt)}</td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-1.5">
                    <button
                      onClick={() => restore.mutate(row)}
                      disabled={restore.isPending}
                      title="Restore"
                      aria-label="Restore"
                      className="inline-flex items-center justify-center w-7 h-7 rounded border border-border text-accent hover:text-white hover:border-accent transition-colors disabled:opacity-40"
                    >
                      <RotateCcw size={14} />
                    </button>
                    {canPurge && (
                      <button
                        onClick={() => { setPurging(row); setConfirmText(''); }}
                        title="Permanently delete"
                        aria-label="Permanently delete"
                        className="inline-flex items-center justify-center w-7 h-7 rounded border btn-ghost-danger transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {restore.error && <p className="text-sm text-rose-400">{(restore.error as Error).message}</p>}

      {purging && (
        <ConfirmModal
          title="Permanently delete"
          message={`Permanently delete "${purging.title}"? This CANNOT be undone — the row and its restorable copy are gone forever. Type DELETE to confirm.`}
          onClose={() => setPurging(null)}
          onConfirm={() => purge.mutate(purging)}
          error={purge.error?.message ?? null}
          submitting={purge.isPending}
          confirmText="Permanently delete"
          confirmDisabled={confirmText !== 'DELETE'}
          confirmClass="bg-rose-700 hover:bg-rose-600 text-oncolor"
        >
          <input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="Type DELETE to confirm"
            className="mt-3 w-full bg-bg-card border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-rose-600"
          />
        </ConfirmModal>
      )}

      {bulkConfirming && (
        <ConfirmModal
          title="Permanently delete selection"
          message={`Permanently delete ${selectedTotal} items (${selectedBreakdown.join(', ')})? This CANNOT be undone — every selected row and its restorable copy are gone forever. Type DELETE to confirm.`}
          onClose={() => setBulkConfirming(false)}
          onConfirm={() => bulkPurge.mutate()}
          error={bulkPurge.error?.message ?? null}
          submitting={bulkPurge.isPending}
          confirmText={`Permanently delete ${selectedTotal}`}
          confirmDisabled={bulkText !== 'DELETE'}
          confirmClass="bg-rose-700 hover:bg-rose-600 text-oncolor"
        >
          <input
            value={bulkText}
            onChange={(e) => setBulkText(e.target.value)}
            placeholder="Type DELETE to confirm"
            className="mt-3 w-full bg-bg-card border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-rose-600"
          />
        </ConfirmModal>
      )}
    </section>
  );
}
